import { useEffect, useState } from "react";

const IS_PROD = import.meta.env.PROD;

export function useScreenProtection() {
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    // ── 1. Disable right-click context menu ──────────────────────────────────
    const blockContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", blockContextMenu);

    // ── 2. Block PrintScreen, screenshot shortcuts, and copy/paste ─────────
    const blockKeys = (e: KeyboardEvent) => {
      const key = e.key?.toLowerCase();

      // PrintScreen
      if (key === "printscreen") {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard?.writeText("").catch(() => {});
        return;
      }

      // Ctrl+C / Cmd+C — copy
      if ((e.ctrlKey || e.metaKey) && key === "c") {
        e.preventDefault();
        return;
      }

      // Ctrl+A / Cmd+A — select all
      if ((e.ctrlKey || e.metaKey) && key === "a") {
        e.preventDefault();
        return;
      }

      // Ctrl+P / Cmd+P — print (which can save as PDF)
      if ((e.ctrlKey || e.metaKey) && key === "p") {
        e.preventDefault();
        return;
      }

      // Windows Snipping Tool: Win+Shift+S
      if (e.metaKey && e.shiftKey && key === "s") {
        e.preventDefault();
        return;
      }

      // Mac screenshot shortcuts: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5
      if (e.metaKey && e.shiftKey && ["3", "4", "5"].includes(key)) {
        e.preventDefault();
        return;
      }
    };
    document.addEventListener("keydown", blockKeys, { capture: true });

    // ── 3. Block clipboard copy via the copy event ────────────────────────
    const blockCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      e.clipboardData?.setData("text/plain", "");
    };
    document.addEventListener("copy", blockCopy, { capture: true });

    // ── 4. Block text selection via mouse ─────────────────────────────────
    const blockSelectStart = (e: Event) => {
      const target = e.target as HTMLElement;
      // Allow selection inside inputs/textareas
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || (target as HTMLElement).isContentEditable) return;
      e.preventDefault();
    };
    document.addEventListener("selectstart", blockSelectStart);

    // ── 5. Detect browser tab screen sharing via getDisplayMedia ─────────
    if (IS_PROD && navigator.mediaDevices?.getDisplayMedia) {
      const original = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
      navigator.mediaDevices.getDisplayMedia = async (constraints?: DisplayMediaStreamOptions) => {
        setCapturing(true);
        const stream = await original(constraints);
        stream.getTracks().forEach(track => {
          track.addEventListener("ended", () => setCapturing(false));
        });
        return stream;
      };
    }

    // ── 6. Disable drag on images ─────────────────────────────────────────
    const blockDrag = (e: DragEvent) => {
      if ((e.target as HTMLElement)?.tagName === "IMG") e.preventDefault();
    };
    document.addEventListener("dragstart", blockDrag);

    // ── 7. Block print dialog (beforeprint event) ─────────────────────────
    const blockPrint = () => {
      // Replace body content with blank during print — restored after
      document.body.style.visibility = "hidden";
    };
    const restoreAfterPrint = () => {
      document.body.style.visibility = "";
    };
    window.addEventListener("beforeprint", blockPrint);
    window.addEventListener("afterprint", restoreAfterPrint);

    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("keydown", blockKeys, { capture: true } as EventListenerOptions);
      document.removeEventListener("copy", blockCopy, { capture: true } as EventListenerOptions);
      document.removeEventListener("selectstart", blockSelectStart);
      document.removeEventListener("dragstart", blockDrag);
      window.removeEventListener("beforeprint", blockPrint);
      window.removeEventListener("afterprint", restoreAfterPrint);
    };
  }, []);

  return { capturing };
}
